// =============================================
// MusicLegalModal — reusable agreement / consent dialog
// =============================================
// Renders a legal document, a "read & agree" checkbox, and a typed-signature field.
// Accept stays disabled until both are satisfied. Used for the one-time service
// agreement (the gate) and the per-upload ownership consent.
// =============================================
import React, { useState } from 'react';
import { FiShield, FiX } from 'react-icons/fi';
import type { LegalDoc } from '../../lib/musicLegal';

interface MusicLegalModalProps {
  doc: LegalDoc;
  intro?: string;
  confirmLabel: string;
  busy?: boolean;
  defaultSignature?: string;
  onAccept: (signatureName: string) => void;
  onCancel: () => void;
  cancelLabel?: string;
}

export const MusicLegalModal: React.FC<MusicLegalModalProps> = ({
  doc, intro, confirmLabel, busy, defaultSignature = '', onAccept, onCancel, cancelLabel = 'Not now',
}) => {
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState(defaultSignature);
  const canAccept = agreed && signature.trim().length >= 2 && !busy;

  return (
    <div className="mlg-overlay" role="dialog" aria-modal="true">
      <div className="mlg-modal">
        <div className="mlg-head">
          <FiShield className="mlg-head-icon" />
          <h3>{doc.title}</h3>
          <button className="mlg-close" onClick={onCancel} title={cancelLabel} disabled={busy}><FiX /></button>
        </div>

        {intro && <p className="mlg-intro">{intro}</p>}

        <div className="mlg-body">
          {doc.body.split('\n\n').map((para, i) => (
            <p key={i} className="mlg-para">{para}</p>
          ))}
          <p className="mlg-version">Version {doc.version}</p>
        </div>

        <label className="mlg-checkbox">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={busy} />
          <span>I have read and agree to the terms above, and I certify the information is true.</span>
        </label>

        <div className="mlg-sign">
          <label>Type your full name as your signature</label>
          <input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="e.g. Sat Mehta"
            disabled={busy}
          />
        </div>

        <div className="mlg-actions">
          <button className="mlg-btn mlg-btn-secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button className="mlg-btn mlg-btn-primary" onClick={() => onAccept(signature.trim())} disabled={!canAccept}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MusicLegalModal;
